import { useId, useMemo } from "react";
import { useTheme, type Theme } from "@repo/tokens";

type DonutChartColors = {
  correctGradientStart: string;
  correctGradientEnd: string;
  incorrectGradientStart: string;
  incorrectGradientEnd: string;
  notAnsweredColor: string;
  backgroundColor: string;
};

function getDonutChartColors(theme: Theme): DonutChartColors {
  return {
    correctGradientStart: theme.colors.success[60],
    correctGradientEnd: theme.colors.success[50],
    incorrectGradientStart: theme.colors.error[60],
    incorrectGradientEnd: theme.colors.error[50],
    notAnsweredColor: theme.colors.neutral[80],
    backgroundColor: theme.colors.neutral[80],
  };
}

export type AttemptPracticeDonutSvgProps = {
  correct: number;
  incorrect: number;
  notAnswered: number;
  size?: number;
  strokeWidth?: number;
};

/**
 * Static SVG donut matching mobile `AttemptDonutSVG` (PracticeReports/DonutChart) end state.
 */
export function AttemptPracticeDonutSvg({
  correct,
  incorrect,
  notAnswered,
  size = 120,
  strokeWidth = 14,
}: AttemptPracticeDonutSvgProps) {
  const { theme } = useTheme();
  const colors = useMemo(() => getDonutChartColors(theme), [theme]);
  const uid = useId().replace(/:/g, "");

  const total = correct + incorrect + notAnswered || 1;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  const correctDash = (correct / total) * circumference;
  const incorrectDash = (incorrect / total) * circumference;
  const notAnsweredDash = (notAnswered / total) * circumference;

  const greenGradId = `greenGrad-${uid}`;
  const redGradId = `redGrad-${uid}`;

  return (
    <svg width={size} height={size} aria-hidden>
      <defs>
        <linearGradient id={greenGradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={colors.correctGradientStart} />
          <stop offset="100%" stopColor={colors.correctGradientEnd} />
        </linearGradient>
        <linearGradient id={redGradId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={colors.incorrectGradientStart} />
          <stop offset="100%" stopColor={colors.incorrectGradientEnd} />
        </linearGradient>
      </defs>
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={colors.backgroundColor}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {correct > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={`url(#${greenGradId})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${correctDash} ${circumference}`}
            strokeDashoffset={0}
          />
        )}
        {incorrect > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={`url(#${redGradId})`}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${incorrectDash} ${circumference}`}
            strokeDashoffset={-correctDash}
          />
        )}
        {notAnswered > 0 && (
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={colors.notAnsweredColor}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={`${notAnsweredDash} ${circumference}`}
            strokeDashoffset={-(correctDash + incorrectDash)}
          />
        )}
      </g>
    </svg>
  );
}
